import {useEffect, useState, useRef} from '@wordpress/element';
import {useStripe, useElements} from "@stripe/react-stripe-js";
import {useSelect, useDispatch} from '@wordpress/data';
import {CART_STORE_KEY, PAYMENT_STORE_KEY, CHECKOUT_STORE_KEY} from '@woocommerce/block-data';
import {isEmail} from '@wordpress/url';
import {
    toCartAddress as mapToCartAddress,
    DEFAULT_BILLING_ADDRESS,
    DEFAULT_SHIPPING_ADDRESS, getSettings, getErrorMessage
} from '../../util';

const toCartAddress = mapToCartAddress();

export const useLink = (
    {
        email,
        phone,
        launchLink,
        popupEnabled,
        shippingPhone
    }) => {
    const [link, setLink] = useState();
    const stripe = useStripe();
    const elements = useElements();
    const currentData = useRef({email, phone, shippingPhone, oldEmail: email, isProcessing: false, launched: false});
    const [debouncedEmail, setDebouncedEmail] = useState(email);

    const {
        __internalSetActivePaymentMethod: setActivePaymentMethod,
        __internalSetPaymentMethodData: setPaymentMethodData,
        __internalSetExpressPaymentError: setExpressPaymentError,
        __internalSetExpressPaymentStarted
    } = useDispatch(PAYMENT_STORE_KEY);

    const {
        __internalSetBeforeProcessing: onSubmit
    } = useDispatch(CHECKOUT_STORE_KEY);

    const {
        setBillingAddress,
        setShippingAddress
    } = useDispatch(CART_STORE_KEY);

    const paymentStatus = useSelect(select => {
        const store = select(PAYMENT_STORE_KEY);
        return {
            isProcessing: store.isPaymentProcessing()
        }
    });

    useEffect(() => {
        currentData.current.email = email;
        currentData.current.phone = phone;
        currentData.current.shippingPhone = shippingPhone;
        currentData.current.isProcessing = paymentStatus.isProcessing;
    }, [email, phone, paymentStatus.isProcessing]);

    useEffect(() => {
        // Set a timer to update the debounced value after 250ms
        const timer = setTimeout(() => {
            setDebouncedEmail(email);
        }, 250);

        // Clean up the timer if email changes before timeout completes
        return () => clearTimeout(timer);
    }, [email]);

    useEffect(() => {
        if (stripe && elements && !link) {
            setLink(stripe.linkAutofillModal(elements));
        }
    }, [
        stripe,
        elements,
        link
    ]);

    useEffect(() => {
        if (link && launchLink) {
            const {email, launched = false} = currentData.current;
            if (!launched && isEmail(email)) {
                link.launch({email});
                currentData.current.launched = true;
            }
        }
    }, [
        link,
        launchLink
    ]);

    useEffect(() => {
        if (!popupEnabled) {
            return;
        }
        const {oldEmail = '', isProcessing = false} = currentData.current;
        if (link && oldEmail !== debouncedEmail && !isProcessing) {
            if (isEmail(debouncedEmail)) {
                link.launch({email: debouncedEmail});
                currentData.current.oldEmail = debouncedEmail;
            }
        }

    }, [
        link,
        popupEnabled,
        debouncedEmail
    ]);

    useEffect(() => {
        if (link) {
            link.on('autofill', async event => {
                currentData.current.event = event;
                const {billingAddress = null, shippingAddress = null} = event.value;
                const {phone, shippingPhone} = currentData.current;
                try {
                    await elements.submit();
                    const result = await stripe.createPaymentMethod({
                        elements,
                        params: {
                            billing_details: billingAddress
                        }
                    });
                    if (result.error) {
                        throw result.error;
                    }

                    if (billingAddress) {
                        setBillingAddress({
                            ...DEFAULT_BILLING_ADDRESS,
                            ...toCartAddress({...billingAddress.address, recipient: billingAddress.name}),
                            email: currentData.current.email,
                            phone
                        });
                    }
                    if (shippingAddress) {
                        setShippingAddress({
                            ...DEFAULT_SHIPPING_ADDRESS,
                            ...toCartAddress({...shippingAddress.address, recipient: shippingAddress.name}),
                            phone: shippingPhone
                        });
                    }

                    setPaymentMethodData({
                        stripe_cc_token_key: result.paymentMethod.id,
                        stripe_cc_save_source_key: false,
                    });
                    onSubmit();
                } catch (error) {
                    // set express error
                    console.log(error);
                    setExpressPaymentError(error.message);
                }
            });
            link.on('authenticated', event => {
                __internalSetExpressPaymentStarted();
                setActivePaymentMethod('stripe_link_checkout');
            });
        }
    }, [
        link,
        stripe,
        elements,
        onSubmit,
        setBillingAddress,
        setShippingAddress,
        setPaymentMethodData,
        setActivePaymentMethod,
        setExpressPaymentError,
        __internalSetExpressPaymentStarted
    ]);
}